// Spirometry: Morris 1971 predicted values and the obstruction label.
// Spec: NGF-formulas-verified-v2.md § Spirometry.
import { round } from './num.ts';
import type { Sex } from './types.ts';

export type Obstruction = 'Normal' | 'Mild' | 'Moderate' | 'Severe' | 'Very Severe';

/** Predicted FVC and FEV1 in litres (unrounded); height in inches, decimal age. */
export function predicted(sex: Sex, heightIn: number, age: number) {
  return sex === 'M'
    ? { fvc: 0.148 * heightIn - 0.025 * age - 4.241, fev1: 0.092 * heightIn - 0.032 * age - 1.24 }
    : { fvc: 0.115 * heightIn - 0.024 * age - 2.852, fev1: 0.089 * heightIn - 0.025 * age - 1.932 };
}

export function obstruction(fev1FvcPct: number): Obstruction {
  if (fev1FvcPct > 80) return 'Normal';
  if (fev1FvcPct >= 65) return 'Mild';
  if (fev1FvcPct >= 50) return 'Moderate';
  if (fev1FvcPct >= 35) return 'Severe';
  return 'Very Severe';
}

export interface SpirometryInput {
  sex: Sex;
  age: number;
  heightIn: number;
  fvc: number;
  fev1: number;
}

export interface SpirometryResult {
  fvcPredicted: number; // L, 0.01
  fev1Predicted: number; // L, 0.01
  fvcPctPredicted: number; // %, 0.01
  fev1PctPredicted: number; // %, 0.01
  fev1FvcPct: number; // measured ratio, %, 0.01
  fev1FvcPctPredicted: number; // %, 0.01
  obstruction: Obstruction;
}

export function scoreSpirometry(x: SpirometryInput): SpirometryResult | null {
  if (!(x.fvc > 0 && x.fev1 > 0 && x.heightIn > 0)) return null;
  const p = predicted(x.sex, x.heightIn, x.age);
  const ratio = (x.fev1 / x.fvc) * 100;
  return {
    fvcPredicted: round(p.fvc, 2),
    fev1Predicted: round(p.fev1, 2),
    fvcPctPredicted: round((x.fvc / p.fvc) * 100, 2),
    fev1PctPredicted: round((x.fev1 / p.fev1) * 100, 2),
    fev1FvcPct: round(ratio, 2),
    fev1FvcPctPredicted: round((p.fev1 / p.fvc) * 100, 2),
    obstruction: obstruction(round(ratio, 2)),
  };
}
